import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Upload, Plus, Trash2, FileText, ExternalLink, File as FileIcon, Image as ImageIcon, ListPlus, Layout as LayoutIcon, Grid3x3, Compass, Zap, Target, Lock, Unlock, EyeOff, Waves } from "lucide-react";
import { toast } from "sonner";
import ContentRequired from "@/components/ContentRequired";
import { useConfirm } from "@/context/ConfirmContext";
import { Link } from "react-router-dom";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Team-fillable canvas templates (private per team). Rendered at the top of Resources.
const CANVAS_CARDS = [
  { slug: "bmc", title: "Business Model Canvas", subtitle: "Nine building blocks · How your solution creates, delivers and captures value", icon: Grid3x3, accent: "#25AAAA", credit: "Strategyzer", creditUrl: "https://www.strategyzer.com//the-business-model-canvas-2" },
  { slug: "vpc", title: "Value Proposition Canvas", subtitle: "Six blocks · Match your offer to the customer's jobs, pains and gains", icon: LayoutIcon, accent: "#F4B847", credit: "Strategyzer", creditUrl: "https://www.strategyzer.com/library/the-value-proposition-canvas" },
  { slug: "mission-lock", title: "Mission Lock Worksheet", subtitle: "Five questions · Test whether your solution is structurally protected from mission drift", icon: Compass, accent: "#0F1F2E", credit: "Eric Ries · Incorruptible", creditUrl: "/lens/mission-lock" },
];

// Weave innovation tools — external links, open in new tab.
const WEAVE_TOOLS = [
  { slug: "awake", url: "https://awake.wearetheweave.co.uk", title: "Awake", subtitle: "Systems thinking · Map interconnections in your challenge", icon: Compass, accent: "#25AAAA" },
  { slug: "catch", url: "https://catch.wearetheweave.co.uk", title: "Catch the Wave", subtitle: "PESTLE horizon scan · Political, Economic, Social, Technological, Legal and Environmental signals shaping the sponsor's landscape", icon: Waves, accent: "#f4b847" },
  { slug: "PISA", url: "https://PISA.wearetheweave.co.uk", title: "PISA", subtitle: "Product · Insight · Strategy · Action — structured innovation planning", icon: Target, accent: "#A23232" },
  { slug: "opportunity-unlock", url: "https://opportunity-unlock.wearetheweave.co.uk", title: "Opportunity Unlock", subtitle: "Spot the real opportunity hidden in the sponsor's brief", icon: Zap, accent: "#F4B847" },
];

// AI tools your teams can use — with a short house-rules blurb per tool.
const AI_TOOLS = [
  { url: "https://chat.openai.com", title: "ChatGPT", subtitle: "House rules: use for divergent brainstorming and rewriting. Never paste sponsor confidential data. Always fact-check quotes." },
  { url: "https://notebooklm.google", title: "NotebookLM", subtitle: "House rules: excellent for synthesising your research PDFs into briefings. Cite the source alongside every claim." },
  { url: "https://www.perplexity.ai", title: "Perplexity", subtitle: "House rules: use for evidence-backed market and competitor research. Follow every cited link before trusting a summary." },
  { url: "https://claude.ai", title: "Claude", subtitle: "House rules: strong at long-form structured writing (problem statements, pitch narratives). Sanity-check figures independently." },
];

// The Mission Lock lens page (Eric Ries · Incorruptible). Highlighted separately.
const LENS_CARDS = [
  { to: "/lens/mission-lock", title: "Mission Lock lens", subtitle: "Eric Ries · Incorruptible — a lens for testing whether your value proposition is structurally protected from mission drift", accent: "#0F1F2E", eyebrow: "Sustainability & mission" },
];

function guessTitleFromUrl(url) {
  try {
    const u = new URL(url);
    const last = u.pathname.split("/").filter(Boolean).pop() || u.hostname;
    return decodeURIComponent(last).replace(/\.[a-z0-9]+$/i, "").replace(/[-_+]/g, " ").slice(0, 80);
  } catch { return url; }
}

export default function Resources() {
  const confirm = useConfirm();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [days, setDays] = useState([]);
  const [open, setOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkDayId, setBulkDayId] = useState("");
  const [bulkText, setBulkText] = useState("");
  const [bulkCategory, setBulkCategory] = useState("");
  const [form, setForm] = useState({ title: "", description: "", external_url: "", category: "", day_id: "" });
  const [fileObj, setFileObj] = useState(null);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState(""); // day id or ""
  const fileRef = useRef(null);
  const isAdmin = user && (user.role === "admin" || user.role === "faculty");

  const load = async () => {
    const [res, dys] = await Promise.all([
      api.get("/resources").then((r) => r.data),
      api.get("/days").then((r) => r.data),
    ]);
    setItems(res);
    setDays(dys);
  };
  useEffect(() => { load(); }, []);

  const filtered = filter
    ? items.filter((r) => r.day_id === filter)
    : items;

  const create = async () => {
    if (!form.title.trim()) return toast.error("Title required");
    setBusy(true);
    try {
      let file_id = null;
      if (fileObj) {
        const fd = new FormData();
        fd.append("file", fileObj);
        const r = await api.post("/files/upload?tag=resource", fd, { headers: { "Content-Type": "multipart/form-data" } });
        file_id = r.data.id;
      }
      await api.post("/resources", { ...form, day_id: form.day_id || null, file_id });
      setOpen(false);
      setForm({ title: "", description: "", external_url: "", category: "", day_id: "" });
      setFileObj(null);
      toast.success("Resource added");
      load();
    } catch (e) { toast.error("Could not add"); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!(await confirm({ title: "Remove this resource?", destructive: true }))) return;
    await api.delete(`/resources/${id}`);
    load();
  };

  const toggleHidden = async (r) => {
    try {
      await api.post(`/resources/${r.id}/toggle-hidden`, { hidden: !r.hidden });
      toast.success(!r.hidden ? "Resource locked" : "Resource revealed");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not toggle");
    }
  };

  const revealDay = async (dayId, dayLabel, hide = false) => {
    if (!dayId) return;
    if (hide && !(await confirm({
      title: `Re-lock all resources for ${dayLabel}?`,
      description: "Students won't see them again until you unlock them.",
      confirmText: "Lock all",
    }))) return;
    try {
      const { data } = await api.post(`/days/${dayId}/reveal-resources`, { hidden: hide });
      toast.success(`${hide ? "Locked" : "Revealed"} ${data.matched} resource${data.matched === 1 ? "" : "s"} for ${dayLabel}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update");
    }
  };

  // ---- Bulk add: parse one resource per non-empty line ----
  // Accepted formats (most permissive):
  //   https://...
  //   Title | https://...
  //   Title | Category | https://...
  //   Day 2 | Title | https://...
  //   Day 2 | Title | Category | https://...
  const parseBulk = () => {
    const lines = bulkText.split("\n").map((l) => l.trim()).filter(Boolean);
    const dayByNumber = Object.fromEntries(days.map((d) => [String(d.day_number), d.id]));
    const out = [];
    for (const raw of lines) {
      const parts = raw.split("|").map((p) => p.trim()).filter(Boolean);
      let url = null, title = null, category = null, day_id = bulkDayId || null;
      // Find a URL among parts; if none, skip
      const urlIdx = parts.findIndex((p) => /^https?:\/\//i.test(p));
      if (urlIdx === -1) continue;
      url = parts[urlIdx];
      const before = parts.slice(0, urlIdx);
      // Detect leading "Day N" token
      if (before.length > 0) {
        const dayMatch = before[0].match(/^day\s*(\d+)$/i);
        if (dayMatch && dayByNumber[dayMatch[1]]) {
          day_id = dayByNumber[dayMatch[1]];
          before.shift();
        }
      }
      title = before[0] || guessTitleFromUrl(url);
      category = before[1] || bulkCategory || null;
      out.push({ title, external_url: url, category, day_id });
    }
    return out;
  };

  const submitBulk = async () => {
    const items = parseBulk();
    if (items.length === 0) {
      toast.error("No valid lines detected — each line needs a URL");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/resources/bulk", { items });
      toast.success(`Added ${data.created} resource${data.created === 1 ? "" : "s"}`);
      setBulkOpen(false);
      setBulkText(""); setBulkDayId(""); setBulkCategory("");
      load();
    } catch (e) {
      toast.error("Bulk add failed");
    } finally { setBusy(false); }
  };

  const previewCount = (() => {
    try { return parseBulk().length; } catch { return 0; }
  })();

  const tokenQuery = `?auth=${encodeURIComponent(localStorage.getItem("weave_token") || "")}`;

  return (
    <div className="space-y-8" data-testid="resources-page">
      {/* Team canvases */}
      <section data-testid="resources-canvases">
        <div className="flex items-baseline justify-between mb-3">
          <div>
            <div className="label-eyebrow">Team canvases</div>
            <h2 className="font-display text-2xl font-black tracking-tight text-[#0F1F2E]">Fill these together, keep them yours</h2>
          </div>
          <p className="text-xs text-[#525252] max-w-sm text-right hidden md:block">Private to your team. Mentors you&apos;ve booked can see them; other teams can&apos;t.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border-l border-t border-[#E5E7EB]">
          {CANVAS_CARDS.map((c) => {
            const Icon = c.icon;
            return (
              <Link
                key={c.slug}
                to={`/canvas/${c.slug}`}
                data-testid={`canvas-card-${c.slug}`}
                className="group border-r border-b border-[#E5E7EB] bg-white p-6 hover:bg-[#F3F4F6] transition-colors"
              >
                <div className="w-12 h-12 flex items-center justify-center" style={{ background: c.accent }}>
                  <Icon size={22} className="text-white"/>
                </div>
                <div className="font-display text-2xl font-black tracking-tight text-[#0F1F2E] mt-4">{c.title}</div>
                <div className="text-sm text-[#525252] mt-2">{c.subtitle}</div>
                {c.credit && (
                  <div className="text-[10px] text-[#525252] mt-2 italic" data-testid={`canvas-card-${c.slug}-credit`}>
                    Framework by <span className="font-semibold not-italic">{c.credit}</span>
                  </div>
                )}
                <div className="mt-4 text-[10px] uppercase tracking-[0.16em] text-[#25AAAA] font-semibold inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                  Open your canvas <ExternalLink size={11}/>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* Weave innovation tools */}
      <section data-testid="resources-weave-tools">
        <div className="mb-3">
          <div className="label-eyebrow">Innovation tools</div>
          <h2 className="font-display text-2xl font-black tracking-tight text-[#0F1F2E]">Weave tools for your team</h2>
          <p className="text-xs text-[#525252] mt-1">External tools built by The Weave. Anything you create in them stays with the tool.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border-l border-t border-[#E5E7EB]">
          {WEAVE_TOOLS.map((t) => {
            const Icon = t.icon;
            return (
              <a
                key={t.url}
                href={t.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`weave-tool-${t.slug}`}
                className="group border-r border-b border-[#E5E7EB] bg-white p-5 hover:bg-[#F3F4F6] transition-colors"
              >
                <div className="w-10 h-10 flex items-center justify-center" style={{ background: t.accent }}>
                  <Icon size={18} className="text-white"/>
                </div>
                <div className="font-display text-xl font-black tracking-tight text-[#0F1F2E] mt-3">{t.title}</div>
                <div className="text-xs text-[#525252] mt-1">{t.subtitle}</div>
                <div className="mt-3 text-[10px] uppercase tracking-[0.16em] text-[#25AAAA] font-semibold inline-flex items-center gap-1">
                  Open in new tab <ExternalLink size={10}/>
                </div>
              </a>
            );
          })}
        </div>
      </section>

      {/* Sustainability & mission lens */}
      <section data-testid="resources-lenses">
        <div className="mb-3">
          <div className="label-eyebrow">Lens for your value proposition</div>
          <h2 className="font-display text-2xl font-black tracking-tight text-[#0F1F2E]">Sustainability &amp; Mission Lock</h2>
          <p className="text-xs text-[#525252] mt-1">A lens to test whether your solution is structurally protected from mission drift — inspired by Eric Ries&apos;s <em>Incorruptible</em>.</p>
        </div>
        <div className="grid grid-cols-1 gap-0 border-l border-t border-[#E5E7EB]">
          {LENS_CARDS.map((c) => (
            <Link key={c.to} to={c.to} data-testid={`lens-card-${c.to.split("/").pop()}`}
              className="group border-r border-b border-[#E5E7EB] bg-white p-6 hover:bg-[#F3F4F6] transition-colors">
              <div className="text-[10px] font-semibold tracking-[0.16em] uppercase text-[#F4B847]">{c.eyebrow}</div>
              <div className="font-display text-2xl font-black tracking-tight text-[#0F1F2E] mt-2">{c.title}</div>
              <div className="text-sm text-[#525252] mt-2 max-w-2xl">{c.subtitle}</div>
              <div className="mt-3 text-[10px] uppercase tracking-[0.16em] text-[#25AAAA] font-semibold inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                Open the lens <ExternalLink size={11}/>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* AI tools */}
      <section data-testid="resources-ai-tools">
        <div className="mb-3">
          <div className="label-eyebrow">AI tools</div>
          <h2 className="font-display text-2xl font-black tracking-tight text-[#0F1F2E]">Use these — with house rules</h2>
          <p className="text-xs text-[#525252] mt-1">Any AI you use is your responsibility. Confidential sponsor data stays off public tools. Always verify what you cite.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0 border-l border-t border-[#E5E7EB]">
          {AI_TOOLS.map((t) => {
            const slug = t.title.toLowerCase().replace(/[^a-z0-9]+/g, "-");
            return (
              <a key={t.url} href={t.url} target="_blank" rel="noopener noreferrer"
                data-testid={`ai-tool-${slug}`}
                className="group border-r border-b border-[#E5E7EB] bg-white p-5 hover:bg-[#F3F4F6] transition-colors">
                <div className="font-display text-xl font-black tracking-tight text-[#0F1F2E]">{t.title}</div>
                <div className="text-xs text-[#525252] mt-2 leading-relaxed">{t.subtitle}</div>
                <div className="mt-3 text-[10px] uppercase tracking-[0.16em] text-[#25AAAA] font-semibold inline-flex items-center gap-1">
                  Open in new tab <ExternalLink size={10}/>
                </div>
              </a>
            );
          })}
        </div>
      </section>

      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-eyebrow">Resource library</div>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-2">Materials for the programme</h1>
          <p className="text-[#525252] mt-2 max-w-2xl text-sm">Company packs, workbook PDFs, slide decks, reading lists, and links — published by the Weave team.</p>
        </div>
        {isAdmin && (
          <div className="flex gap-2">
            <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="rounded-none border-[#0A0A0A]" data-testid="resource-bulk-btn">
                  <ListPlus size={16} className="mr-2" /> Bulk add
                </Button>
              </DialogTrigger>
              <DialogContent className="rounded-none max-w-3xl">
                <DialogHeader>
                  <DialogTitle className="font-display tracking-tight">Bulk add resources</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="text-sm text-[#525252]">
                    <p>Paste one resource per line. Each line just needs to contain a URL.</p>
                    <p className="mt-2">Accepted formats (most flexible first):</p>
                    <ul className="mt-2 text-xs font-mono space-y-1 bg-[#F3F4F6] p-3 border border-[#E5E7EB]">
                      <li>https://www.dropbox.com/scl/fi/.../my-deck.pdf</li>
                      <li>Speaker deck | https://drive.google.com/file/d/...</li>
                      <li>Day 2 | Speaker deck | https://drive.google.com/...</li>
                      <li>Day 3 | ESG brief | Brief | https://www.dropbox.com/...</li>
                    </ul>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="label-eyebrow">Default day</Label>
                      <select
                        className="mt-2 w-full h-11 border border-[#E5E7EB] bg-white px-3 text-sm"
                        value={bulkDayId}
                        onChange={(e) => setBulkDayId(e.target.value)}
                        data-testid="resource-bulk-day"
                      >
                        <option value="">— None / use line tag —</option>
                        {days.map((d) => (
                          <option key={d.id} value={d.id}>Day {d.day_number}{d.title ? ` · ${d.title}` : ""}</option>
                        ))}
                      </select>
                      <p className="text-[11px] text-[#525252] mt-1">Used when a line doesn&apos;t start with &quot;Day N |&quot;</p>
                    </div>
                    <div>
                      <Label className="label-eyebrow">Default category</Label>
                      <Input
                        value={bulkCategory}
                        onChange={(e) => setBulkCategory(e.target.value)}
                        className="mt-2 rounded-none h-11"
                        placeholder="e.g. Deck / Brief / Reading"
                        data-testid="resource-bulk-category"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="label-eyebrow">Lines</Label>
                    <Textarea
                      value={bulkText}
                      onChange={(e) => setBulkText(e.target.value)}
                      placeholder={"Day 1 | Founding business pack | https://www.dropbox.com/scl/...\nDay 2 | Strategy reading | https://drive.google.com/file/d/..."}
                      className="mt-2 rounded-none min-h-[220px] font-mono text-sm"
                      data-testid="resource-bulk-textarea"
                    />
                    <div className="text-[11px] text-[#525252] mt-2">
                      {previewCount} resource{previewCount === 1 ? "" : "s"} will be created.
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button onClick={submitBulk} disabled={busy || previewCount === 0} className="rounded-none bg-[#2596be] hover:bg-[#1e7da3]" data-testid="resource-bulk-save-btn">
                    Create {previewCount > 0 ? `${previewCount} ` : ""}resource{previewCount === 1 ? "" : "s"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-none bg-[#2596be] hover:bg-[#1e7da3]" data-testid="resource-add-btn">
                <Plus size={16} className="mr-2" /> Add resource
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-none max-w-2xl">
              <DialogHeader>
                <DialogTitle className="font-display tracking-tight">New resource</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <Field label="Title" value={form.title} onChange={(v) => setForm((f) => ({ ...f, title: v }))} />
                <div>
                  <Label className="label-eyebrow">Description</Label>
                  <Textarea className="mt-2 rounded-none min-h-[80px]" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Category" value={form.category} onChange={(v) => setForm((f) => ({ ...f, category: v }))} placeholder="Workbook · Deck · Pack…" />
                  <div>
                    <Label className="label-eyebrow">Day (optional)</Label>
                    <select
                      className="mt-2 w-full h-11 border border-[#E5E7EB] bg-white px-3 text-sm"
                      value={form.day_id || ""}
                      onChange={(e) => setForm((f) => ({ ...f, day_id: e.target.value }))}
                      data-testid="resource-day-select"
                    >
                      <option value="">— Any / general —</option>
                      {days.map((d) => (
                        <option key={d.id} value={d.id}>Day {d.day_number}{d.title ? ` · ${d.title}` : ""}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <Field label="External URL (e.g. Dropbox / Google Drive share link)" value={form.external_url} onChange={(v) => setForm((f) => ({ ...f, external_url: v }))} placeholder="https://www.dropbox.com/scl/fi/…" />
                <div>
                  <Label className="label-eyebrow">Upload file (optional)</Label>
                  <input ref={fileRef} type="file" onChange={(e) => setFileObj(e.target.files[0])} className="mt-2 block w-full text-sm" data-testid="resource-file-input" />
                  {fileObj && <div className="text-xs text-[#525252] mt-1">{fileObj.name}</div>}
                </div>
              </div>
              <DialogFooter>
                <Button onClick={create} disabled={busy} className="rounded-none bg-[#2596be] hover:bg-[#1e7da3]" data-testid="resource-save-btn">
                  {busy ? "Saving…" : "Add resource"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          </div>
        )}
      </div>

      {/* Day filter chips */}
      {days.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap" data-testid="resources-day-filter">
          <button
            onClick={() => setFilter("")}
            data-testid="resources-filter-all"
            className={`px-3 py-1.5 text-xs uppercase tracking-wider border ${
              filter === "" ? "border-[#0A0A0A] bg-[#0A0A0A] text-white" : "border-[#E5E7EB] bg-white hover:border-[#0A0A0A]"
            }`}
          >
            All ({items.length})
          </button>
          {days.map((d) => {
            const count = items.filter((r) => r.day_id === d.id).length;
            const hiddenCount = items.filter((r) => r.day_id === d.id && r.hidden).length;
            return (
              <div key={d.id} className="inline-flex items-stretch">
                <button
                  onClick={() => setFilter(d.id)}
                  data-testid={`resources-filter-day-${d.day_number}`}
                  className={`px-3 py-1.5 text-xs uppercase tracking-wider border ${
                    filter === d.id ? "border-[#0A0A0A] bg-[#0A0A0A] text-white" : "border-[#E5E7EB] bg-white hover:border-[#0A0A0A]"
                  } ${isAdmin && count > 0 ? "border-r-0" : ""}`}
                >
                  Day {d.day_number} ({count})
                  {isAdmin && hiddenCount > 0 && (
                    <span
                      className={`ml-1.5 inline-flex items-center gap-0.5 ${filter === d.id ? "text-amber-200" : "text-amber-700"}`}
                      title={`${hiddenCount} locked`}
                    >
                      <Lock size={10} /> {hiddenCount}
                    </span>
                  )}
                </button>
                {isAdmin && count > 0 && (
                  hiddenCount > 0 ? (
                    <button
                      onClick={() => revealDay(d.id, `Day ${d.day_number}`, false)}
                      title={`Reveal all ${hiddenCount} locked resource${hiddenCount === 1 ? "" : "s"} for Day ${d.day_number}`}
                      data-testid={`resources-reveal-day-${d.day_number}`}
                      className="px-2 py-1.5 text-xs border border-emerald-500 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 inline-flex items-center"
                    >
                      <Unlock size={12} />
                    </button>
                  ) : (
                    <button
                      onClick={() => revealDay(d.id, `Day ${d.day_number}`, true)}
                      title={`Re-lock all ${count} resource${count === 1 ? "" : "s"} for Day ${d.day_number}`}
                      data-testid={`resources-lock-day-${d.day_number}`}
                      className="px-2 py-1.5 text-xs border border-slate-300 bg-white text-slate-500 hover:bg-slate-50 inline-flex items-center"
                    >
                      <Lock size={12} />
                    </button>
                  )
                )}
              </div>
            );
          })}
          {items.some((r) => !r.day_id) && (
            <button
              onClick={() => setFilter("__general__")}
              data-testid="resources-filter-general"
              className={`px-3 py-1.5 text-xs uppercase tracking-wider border ${
                filter === "__general__" ? "border-[#0A0A0A] bg-[#0A0A0A] text-white" : "border-[#E5E7EB] bg-white hover:border-[#0A0A0A]"
              }`}
            >
              General ({items.filter((r) => !r.day_id).length})
            </button>
          )}
        </div>
      )}

      {items.length === 0 ? (
        <ContentRequired
          title="No resources uploaded yet"
          description="Upload company packs, workbook PDFs, presentation slides, handouts, reading lists, Castmagic outputs, or paste links to Dropbox / Google Drive / SharePoint files. Tag each resource to a day so it shows up on that day's page automatically."
          needed="Company packs · workbook PDFs · slides · handouts · reading lists · Dropbox links"
          where="Resource library + linked from day pages"
          format="PDF, DOCX, PPTX, images, or external URLs"
          testid="resources-empty-content-required"
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {(filter === "__general__" ? items.filter((r) => !r.day_id) : filtered).map((r) => {
            const day = days.find((d) => d.id === r.day_id);
            return (
            <div key={r.id} className={`border ${r.hidden ? "border-amber-200 bg-amber-50/30" : "border-[#E5E7EB] bg-white"} p-5 flex flex-col`} data-testid={`resource-card-${r.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="label-eyebrow">{r.category || "Resource"}</span>
                  {day && (
                    <span className="text-[10px] uppercase tracking-wider border border-[#2596be] text-[#1e7da3] bg-[#E6F4FA] px-2 py-0.5">
                      Day {day.day_number}
                    </span>
                  )}
                  {r.hidden && (
                    <span className="text-[10px] uppercase tracking-wider border border-amber-400 text-amber-800 bg-amber-100 px-2 py-0.5 inline-flex items-center gap-1" data-testid={`resource-locked-badge-${r.id}`}>
                      <Lock size={10} /> Locked
                    </span>
                  )}
                </div>
                {isAdmin && (
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => toggleHidden(r)}
                      title={r.hidden ? "Reveal this resource to students" : "Lock this resource"}
                      data-testid={`resource-toggle-hidden-${r.id}`}
                      className={`p-1 ${r.hidden ? "text-amber-700 hover:bg-amber-100" : "text-emerald-600 hover:bg-emerald-100"}`}
                    >
                      {r.hidden ? <Lock size={14} /> : <Unlock size={14} />}
                    </button>
                    <button onClick={() => remove(r.id)} className="text-[#525252] hover:text-[#e93e36]" data-testid={`resource-delete-${r.id}`}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                )}
              </div>
              <div className="font-display text-lg font-bold tracking-tight mt-3">{r.title}</div>
              {r.description && <p className="text-sm text-[#525252] mt-2 flex-1">{r.description}</p>}
              <div className="mt-4 flex items-center gap-3 text-sm">
                {r.file_id && (
                  <a href={`${BACKEND}/api/files/${r.file_id}/download${tokenQuery}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#2596be] hover:underline" data-testid={`resource-file-${r.id}`}>
                    <FileText size={14} /> Open file
                  </a>
                )}
                {r.external_url && (
                  <a href={r.external_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#2596be] hover:underline" data-testid={`resource-link-${r.id}`}>
                    <ExternalLink size={14} /> Open link
                  </a>
                )}
              </div>
            </div>
          );
          })}
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, placeholder }) {
  return (
    <div>
      <Label className="label-eyebrow">{label}</Label>
      <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="mt-2 rounded-none h-11" />
    </div>
  );
}
